import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, RotateCcwIcon } from 'lucide-react';
import { Diagnosis } from '../utils/diagnosis';
import { ScoreRing } from './ScoreRing';
import { Footer } from './Footer';
type ResultProps = {
  diagnosis: Diagnosis;
  onContinue: () => void;
  onRestart: () => void;
};
const card = (index: number) => ({
  initial: {
    opacity: 0,
    y: 24
  },
  animate: {
    opacity: 1,
    y: 0
  },
  transition: {
    duration: 0.9,
    delay: 0.7 + index * 0.18,
    ease: [0.22, 1, 0.36, 1] as const
  }
});
export function Result({
  diagnosis,
  onContinue,
  onRestart
}: ResultProps) {
  return <div className="w-full bg-beige-50" data-id="element-88" data-mp="1zxi5j2lqmxjg">
      <section className="mx-auto w-full max-w-2xl px-6 pb-24 pt-20 sm:px-10 sm:pt-28" data-id="element-89" data-mp="1zykx7cqvx40f">
        <motion.p initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.9,
        ease: [0.22, 1, 0.36, 1]
      }} className="text-center text-[0.7rem] uppercase tracking-[0.32em] text-sage-600" data-id="element-90" data-mp="23zwn7jdo7r95">
          Tu Skin Fresh Index™
        </motion.p>

        <div className="mt-12" data-id="element-91" data-mp="3g4rsyp1c19de">
          <ScoreRing score={diagnosis.score} data-id="element-92" data-mp="3g4i97j6vryg5" />
        </div>

        <motion.h1 initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.9,
        delay: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }} className="mx-auto mt-12 max-w-lg text-center font-serif text-3xl font-light leading-[1.15] text-warmgray-800 sm:text-[2.5rem]" data-id="element-93" data-mp="3c36j7ck3hb7f">
          {diagnosis.headline}
        </motion.h1>

        <div className="mt-16 grid grid-cols-1 gap-5" data-id="element-94" data-mp="3phfk55n492s">
          {/* 1 — Fortalezas */}
          <motion.article {...card(0)} className="rounded-4xl border border-beige-300 bg-white px-7 py-9 sm:px-10 sm:py-11" data-id="element-95" data-mp="3v07qx5519pf2">
            <h2 className="font-serif text-2xl font-light text-warmgray-800" data-id="element-96" data-mp="rd7cf6esd86e">
              Tu piel está funcionando bien en
            </h2>
            <ul className="mt-7 flex flex-col gap-4" data-id="element-97" data-mp="mkxsq849l4ck">
              {diagnosis.strengths.map(item => <li key={item.key} className="flex items-center gap-4" data-id="element-98" data-mp="15kkee0h0l3iw">
                  <span aria-hidden="true" className="text-sage-600" data-id="element-99" data-mp="1ah2hyx0lyvtn">
                    ✔
                  </span>
                  <span className="flex-1 text-base font-light text-warmgray-800" data-id="element-100" data-mp="wpxgrqhldvh7">
                    {item.label}
                  </span>
                  <span className="text-xs font-light tabular-nums text-warmgray-400" data-id="element-101" data-mp="mrgjycvz2mn8">
                    {item.score}
                  </span>
                </li>)}
            </ul>
          </motion.article>

          {/* 2 — Mejoras */}
          <motion.article {...card(1)} className="rounded-4xl border border-sage-200 bg-sage-50 px-7 py-9 sm:px-10 sm:py-11" data-id="element-102" data-mp="dt4ifftki4g0">
            <h2 className="font-serif text-2xl font-light text-warmgray-800" data-id="element-103" data-mp="2852r9gnysybd">
              Mejoras recomendadas
            </h2>
            <ul className="mt-7 flex flex-col gap-5" data-id="element-104" data-mp="2c6edrdbjl3fc">
              {diagnosis.improvements.map(item => <li key={item.key} data-id="element-105" data-mp="hhvv7asqqpk">
                  <div className="flex items-baseline justify-between" data-id="element-106" data-mp="3snn0sxyf2xja">
                    <span className="text-base font-light text-warmgray-800" data-id="element-107" data-mp="3slk2zrr0w040">
                      {item.label}
                    </span>
                    <span className="text-xs font-light tabular-nums text-warmgray-400" data-id="element-108" data-mp="5ifbbc4f96nd">
                      {item.score}
                    </span>
                  </div>
                  <div className="mt-2 h-px w-full bg-sage-200" data-id="element-109" data-mp="f8gz02sv4n1r">
                    <motion.div className="h-px bg-sage-500" initial={{
                  width: 0
                }} animate={{
                  width: `${item.score}%`
                }} transition={{
                  duration: 1.4,
                  delay: 1.2,
                  ease: [0.22, 1, 0.36, 1]
                }} data-id="element-110" data-mp="f87cip710y0t" />
                  </div>
                </li>)}
            </ul>
          </motion.article>

          {/* 3 — Enfoque */}
          <motion.article {...card(2)} className="rounded-4xl border border-beige-300 bg-white px-7 py-9 sm:px-10 sm:py-11" data-id="element-111" data-mp="2wgs3fkfiqbg0">
            <h2 className="font-serif text-2xl font-light text-warmgray-800" data-id="element-112" data-mp="31gei7yazvzqx">
              Tu piel necesita enfocarse en
            </h2>
            <ul className="mt-7 flex flex-col divide-y divide-beige-200 border-y border-beige-200" data-id="element-113" data-mp="28r7nzbikpwbh">
              {diagnosis.focus.map(item => <li key={item} className="flex items-start gap-4 py-4" data-id="element-114" data-mp="2dr290yfo3rom">
                  <span aria-hidden="true" className="mt-0.5 text-sage-600" data-id="element-115" data-mp="2ip3tnnsqrz27">
                    ✦
                  </span>
                  <span className="text-base font-light leading-relaxed text-warmgray-800" data-id="element-116" data-mp="2ngvmy63a8iw1">
                    {item}
                  </span>
                </li>)}
            </ul>
          </motion.article>

          {/* 4 — Lectura */}
          <motion.article {...card(3)} className="overflow-hidden rounded-4xl border border-beige-300 bg-white" data-id="element-117" data-mp="1ng09dijh8lvj">
            <img src="https://cdn.magicpatterns.com/patterns/generated-images/44db1689-4609-4c70-a3dc-888b75c3ad6e.jpg" alt="" aria-hidden="true" className="h-32 w-full object-cover sm:h-40" data-id="element-118" data-mp="1ihqdf5pxhh8v" />
            <div className="px-7 py-9 sm:px-10 sm:py-11" data-id="element-119" data-mp="14hxqllhivl66">
              <h2 className="font-serif text-2xl font-light text-warmgray-800" data-id="element-120" data-mp="14jijz7s3oy2g">
                Cómo leer tu resultado
              </h2>
              <p className="mt-5 text-sm font-light leading-relaxed text-warmgray-600 sm:text-base" data-id="element-121" data-mp="ur32ebkpubi3">
                {diagnosis.explanation}
              </p>
              <p className="mt-5 text-sm font-light leading-relaxed text-warmgray-600 sm:text-base" data-id="element-122" data-mp="pqf2f0eiekbq">
                Pequeñas mejoras sostenidas en estas áreas generan una diferencia
                visible con el tiempo: no se trata de sumar productos, sino de
                ordenar los gestos correctos en el momento correcto.
              </p>
            </div>
          </motion.article>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.9,
        delay: 1.4,
        ease: [0.22, 1, 0.36, 1]
      }} className="mt-16 flex flex-col items-center" data-id="element-123" data-mp="b3q6t9ndoram">
          <button type="button" onClick={onContinue} className="group flex w-full items-center justify-center gap-3 rounded-full bg-sage-700 px-10 py-6 text-sm uppercase tracking-[0.16em] text-beige-50 transition-all duration-700 ease-calm hover:bg-sage-800 sm:w-auto sm:px-14" data-id="element-124" data-mp="pugz0isvtse9">
            Continuar
            <ArrowRightIcon className="h-4 w-4 transition-transform duration-700 ease-calm group-hover:translate-x-1" strokeWidth={1.25} data-id="element-125" data-mp="l1w7xp1d1tef" />
          </button>

          <button type="button" onClick={onRestart} className="mt-10 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-warmgray-400 transition-colors duration-500 hover:text-sage-700" data-id="element-126" data-mp="1dme8424y66bo">
            <RotateCcwIcon className="h-3.5 w-3.5" strokeWidth={1.25} data-id="element-127" data-mp="1wp6aalc9fz24" />
            Repetir el diagnóstico
          </button>
        </motion.div>
      </section>

      <Footer data-id="element-128" data-mp="1rs666fyz16dn" />
    </div>;
}